import { useState } from 'react';
import { Form, message } from 'antd';
import dayjs from 'dayjs';
import { generateUniqueStepCode } from '../../../utils/sopUtils';

// 从步骤的actionParams解析出messageList
export const parseMessageListFromStep = (step) => {
  if (!step) return [];
  
  // 如果已经有messageList，直接返回
  if (step.messageList && Array.isArray(step.messageList) && step.messageList.length > 0) {
    return step.messageList;
  }
  
  // 解析actionParams
  let actionParams = step.actionParams;
  
  // 如果actionParams是字符串，尝试解析
  if (typeof actionParams === 'string') {
    try {
      actionParams = JSON.parse(actionParams);
    } catch(e) {
      console.error('解析步骤actionParams失败:', e);
      return [];
    }
  }
  
  // 如果actionParams不是对象，返回空数组
  if (!actionParams || typeof actionParams !== 'object') {
    return [];
  }
  
  const messageList = [];
  
  // 处理文本内容
  if (actionParams.text) {
    messageList.push({
      id: 'text-' + Date.now() + Math.random(),
      type: 'TEXT',
      params: {
        content: actionParams.text
      }
    });
  }
  
  // 收集图片附件，稍后统一处理
  const imageAttachments = [];
  
  // 处理附件
  if (actionParams.attachments && Array.isArray(actionParams.attachments)) {
    // 第一步：收集所有附件，区分图片和其他类型
    actionParams.attachments.forEach(attachment => {
      if (attachment.msgtype === 'image') {
        // 收集图片附件，但不直接添加到messageList
        imageAttachments.push({
          mediaId: attachment.image.media_id || attachment.image.pic_url,
          ossUrl: attachment.image.oss_url || ''
        });
      } else if (attachment.msgtype === 'video') {
        messageList.push({
          id: 'video-' + Date.now() + Math.random(),
          type: 'VIDEO',
          params: {
            mediaId: attachment.video.media_id,
            ossUrl: attachment.video.oss_url || attachment.video.ossUrl || ''
          }
        });
      } else if (attachment.msgtype === 'file') {
        messageList.push({
          id: 'file-' + Date.now() + Math.random(),
          type: 'FILE',
          params: {
            mediaId: attachment.file.media_id,
            ossUrl: attachment.file.oss_url || ''
          }
        });
      } else if (attachment.msgtype === 'link') {
        messageList.push({
          id: 'link-' + Date.now() + Math.random(),
          type: 'LINK',
          params: {
            title: attachment.link.title || '',
            url: attachment.link.url || '',
            description: attachment.link.desc || '',
            picUrl: attachment.link.picurl || '',
            picUrl_ossUrl: attachment.link.pic_oss_url || ''
          }
        });
      } else if (attachment.msgtype === 'miniprogram') {
        messageList.push({
          id: 'miniprogram-' + Date.now() + Math.random(),
          type: 'MINIPROGRAM',
          params: {
            title: attachment.miniprogram.title || '',
            appid: attachment.miniprogram.appid || '',
            pagepath: attachment.miniprogram.page || '',
            thumbMediaId: attachment.miniprogram.pic_media_id || '',
            thumbMediaId_ossUrl: attachment.miniprogram.pic_oss_url || ''
          }
        });
      }
    });
    
    // 第二步：处理图片附件
    if (imageAttachments.length > 0) {
      // 1. 将每张图片添加为单独的图片项 - 用于API保存和单图模式显示
      imageAttachments.forEach(img => {
        messageList.push({
          id: 'image-' + Date.now() + Math.random(),
          type: 'IMAGE',
          params: {
            mediaId: img.mediaId,
            ossUrl: img.ossUrl
          }
        });
      });
      
      // 2. 如果有多张图片，创建图片组 - 用于图片组模式显示
      if (imageAttachments.length > 1) {
        // 多张图片，创建图片组
        const groupId = 'image-group-' + Date.now();
        const imageGroup = {
          id: groupId,
          type: 'IMAGE_GROUP',
          params: {
            imageGroup: {
              mediaIds: [],
              ossUrls: {}
            }
          }
        };
        
        // 添加所有图片到图片组
        imageAttachments.forEach(img => {
          imageGroup.params.imageGroup.mediaIds.push(img.mediaId);
          imageGroup.params.imageGroup.ossUrls[img.mediaId] = img.ossUrl;
        });
        
        // 将图片组添加到消息列表
        messageList.push(imageGroup);
        
        console.log('创建图片组，包含', imageAttachments.length, '张图片:', imageGroup);
      }
    }
  }
  
  return messageList;
};

// 从步骤的actionParams解析出momentsList
export const parseMomentsListFromStep = (step) => {
  if (!step) return [];
  
  // 如果已经有momentsList，直接返回
  if (step.momentsList && Array.isArray(step.momentsList) && step.momentsList.length > 0) {
    return step.momentsList;
  }
  
  // 解析actionParams
  let actionParams = step.actionParams;
  
  // 如果actionParams是字符串，尝试解析
  if (typeof actionParams === 'string') {
    try {
      actionParams = JSON.parse(actionParams);
    } catch(e) {
      console.error('解析步骤actionParams失败:', e);
      return [];
    }
  }
  
  // 如果actionParams不是对象，返回空数组
  if (!actionParams || typeof actionParams !== 'object') {
    return [];
  }
  
  const momentsList = [];
  
  // 处理文本内容
  if (actionParams.text && actionParams.text.content) {
    momentsList.push({
      id: 'moment-text-' + Date.now() + Math.random(),
      type: 'MOMENT_TEXT',
      params: {
        content: actionParams.text.content
      }
    });
  }
  
  // 收集图片附件，稍后统一处理
  const imageAttachments = [];
  
  // 处理附件
  if (actionParams.attachments && Array.isArray(actionParams.attachments)) {
    // 第一步：收集所有附件，区分图片和其他类型
    actionParams.attachments.forEach(attachment => {
      if (attachment.msgtype === 'image') {
        // 收集图片附件
        imageAttachments.push({
          mediaId: attachment.image.media_id,
          ossUrl: attachment.image.oss_url || ''
        });
      } else if (attachment.msgtype === 'video') {
        momentsList.push({
          id: 'moment-video-' + Date.now() + Math.random(),
          type: 'MOMENT_VIDEO',
          params: {
            mediaId: attachment.video.media_id,
            ossUrl: attachment.video.oss_url || attachment.video.ossUrl || ''
          }
        });
      } else if (attachment.msgtype === 'link') {
        momentsList.push({
          id: 'moment-link-' + Date.now() + Math.random(),
          type: 'MOMENT_LINK',
          params: {
            title: attachment.link.title || '',
            url: attachment.link.url || '',
            description: attachment.link.desc || '',
            picUrl: attachment.link.picurl || '',
            picOssUrl: attachment.link.pic_oss_url || ''
          }
        });
      }
    });
    
    // 第二步：处理图片附件 - 如果有多张图片，创建图片组
    if (imageAttachments.length > 0) {
      if (imageAttachments.length === 1) {
        // 单张图片，保持原样
        momentsList.push({
          id: 'moment-image-' + Date.now() + Math.random(),
          type: 'MOMENT_IMAGE',
          params: {
            mediaId: imageAttachments[0].mediaId,
            ossUrl: imageAttachments[0].ossUrl
          }
        });
      } else {
        // 多张图片，创建图片组
        const groupId = 'moment-image-group-' + Date.now();
        const imageGroup = {
          id: groupId,
          type: 'MOMENT_IMAGE_GROUP',
          params: {
            imageGroup: {
              mediaIds: [],
              ossUrls: {}
            }
          }
        };
        
        // 添加所有图片到图片组
        imageAttachments.forEach(img => {
          imageGroup.params.imageGroup.mediaIds.push(img.mediaId);
          imageGroup.params.imageGroup.ossUrls[img.mediaId] = img.ossUrl;
        });
        
        // 将图片组添加到朋友圈列表
        momentsList.push(imageGroup);
        
        console.log('创建朋友圈图片组，包含', imageAttachments.length, '张图片:', imageGroup);
      }
    }
  }
  
  return momentsList;
};

export const useScheduledStepManagement = (steps, setSteps, messageList, setMessageList, setPreviewUrlsState) => {
  const [currentStep, setCurrentStep] = useState(null);
  const [isStepModalVisible, setIsStepModalVisible] = useState(false);
  const [stepForm] = Form.useForm();

  // 辅助函数：标准化日期对象为dayjs对象
  const normalizeDateToDayjs = (dateValue) => {
    if (!dateValue) return null;
    
    // 已经是dayjs对象
    if (dayjs.isDayjs(dateValue)) return dateValue;
    
    // 是moment对象
    if (typeof dateValue === 'object' && dateValue._isAMomentObject) {
      try {
        return dayjs(dateValue.format('YYYY-MM-DD'));
      } catch (e) {
        console.error('转换moment日期对象失败:', e);
        return dayjs(); // 默认返回当前日期
      }
    }
    
    // 字符串或其他类型
    try {
      return dayjs(dateValue);
    } catch (e) {
      console.error('转换日期值失败:', e, dateValue);
      return dayjs(); // 默认返回当前日期
    }
  };
  
  // 辅助函数：标准化时间对象为dayjs对象
  const normalizeTimeToDayjs = (timeValue) => {
    if (!timeValue) return null;
    
    // 已经是dayjs对象
    if (dayjs.isDayjs(timeValue)) return timeValue;
    
    // 是moment对象
    if (typeof timeValue === 'object' && timeValue._isAMomentObject) {
      try {
        return dayjs(timeValue.format('HH:mm:ss'), 'HH:mm:ss');
      } catch (e) {
        console.error('转换moment时间对象失败:', e);
        return dayjs('09:00:00', 'HH:mm:ss'); // 默认返回9点
      }
    }
    
    // 字符串
    if (typeof timeValue === 'string') {
      try {
        return dayjs(timeValue, 'HH:mm:ss');
      } catch (e) {
        console.error('转换时间字符串失败:', e, timeValue);
        return dayjs('09:00:00', 'HH:mm:ss'); // 默认返回9点
      }
    }
    
    // 其他情况返回默认值
    return dayjs('09:00:00', 'HH:mm:ss');
  };

  // 添加步骤
  const handleAddStep = () => {
    setCurrentStep(null);
    stepForm.resetFields();
    setMessageList([]);
    setIsStepModalVisible(true);
  };

  // 编辑步骤
  const handleEditStep = (step) => {
    console.log('开始编辑步骤:', step);
    
    // 创建一个新的步骤对象，避免直接修改原始对象
    const processedStep = { ...step };
    
    // 确保保留原始actionParams字符串
    if (typeof step.actionParams === 'string') {
      // 如果actionParams是字符串形式，直接保存原始字符串
      processedStep.originalActionParams = step.actionParams;
    } else if (typeof step.actionParams === 'object' && step.originalActionParams) {
      // 如果已经有originalActionParams，继续保留
      processedStep.originalActionParams = step.originalActionParams;
    }
    
    // 确保 type 字段不为空，根据 actionType 推断 type
    if (!processedStep.type) {
      console.log('步骤type为空，从actionType推断type');
      if (processedStep.actionType === 'SEND_MESSAGE') {
        processedStep.type = 'MESSAGE';
      } else if (processedStep.actionType === 'SEND_MOMENT') {
        processedStep.type = 'MOMENTS';
      } else if (processedStep.actionType === 'UPDATE_TAG' || processedStep.actionType === 'ADD_TAG' || processedStep.actionType === 'REMOVE_TAG') {
        processedStep.type = 'TAG';
      } else {
        // 默认设为消息类型
        processedStep.type = 'MESSAGE';
      }
      console.log('推断出的type:', processedStep.type);
    }
    
    // 记录原始日期时间值，便于调试
    console.log('原始日期和时间值:', {
      scheduledDate: processedStep.scheduledDate,
      scheduledTime: processedStep.scheduledTime,
      scheduledDateType: processedStep.scheduledDate ? typeof processedStep.scheduledDate : 'null',
      scheduledTimeType: processedStep.scheduledTime ? typeof processedStep.scheduledTime : 'null',
      isDayjsDate: processedStep.scheduledDate && dayjs.isDayjs(processedStep.scheduledDate),
      isDayjsTime: processedStep.scheduledTime && dayjs.isDayjs(processedStep.scheduledTime),
      isMomentDate: processedStep.scheduledDate && processedStep.scheduledDate._isAMomentObject,
      isMomentTime: processedStep.scheduledTime && processedStep.scheduledTime._isAMomentObject,
      actionParams: processedStep.actionParams,
      originalActionParams: processedStep.originalActionParams
    });
    
    // 解析actionParams（如果是字符串）
    let actionParams = processedStep.actionParams;
    if (typeof actionParams === 'string') {
      try {
        actionParams = JSON.parse(actionParams);
        processedStep.actionParams = actionParams;
      } catch(e) {
        console.warn(`步骤 ${processedStep.id || processedStep.stepCode} actionParams解析失败:`, e);
        actionParams = {};
      }
    } else if (!actionParams) {
      actionParams = {};
    }
    
    // 确保步骤有 messageList 和 momentsList 属性
    if (processedStep.type === 'MESSAGE' && (!processedStep.messageList || processedStep.messageList.length === 0)) {
      processedStep.messageList = parseMessageListFromStep(processedStep);
      console.log('从actionParams解析messageList:', processedStep.messageList);
    }
    
    if (processedStep.type === 'MOMENTS' && (!processedStep.momentsList || processedStep.momentsList.length === 0)) {
      processedStep.momentsList = parseMomentsListFromStep(processedStep);
      console.log('从actionParams解析momentsList:', processedStep.momentsList);
    }
    
    setCurrentStep(processedStep);
    
    // 设置步骤表单初始值
    const initialValues = {
      name: processedStep.name,
      type: processedStep.type,
      description: processedStep.description || '',
      actionType: processedStep.actionType,
      actionParams: actionParams
    };
    
    // 处理日期时间 - 确保使用标准化函数
    initialValues.scheduledDate = normalizeDateToDayjs(processedStep.scheduledDate);
    initialValues.scheduledTime = normalizeTimeToDayjs(processedStep.scheduledTime);
    
    console.log('处理后的日期和时间值:', {
      scheduledDate: initialValues.scheduledDate,
      scheduledTime: initialValues.scheduledTime,
      isDayjsDate: initialValues.scheduledDate && dayjs.isDayjs(initialValues.scheduledDate),
      isDayjsTime: initialValues.scheduledTime && dayjs.isDayjs(initialValues.scheduledTime),
      dateFormat: initialValues.scheduledDate ? initialValues.scheduledDate.format('YYYY-MM-DD') : 'null',
      timeFormat: initialValues.scheduledTime ? initialValues.scheduledTime.format('HH:mm:ss') : 'null'
    });
    
    // 为MESSAGE类型的TEXT子类型特别处理content字段
    if (processedStep.type === 'MESSAGE') {
      // 从messageList中查找TEXT类型的消息
      if (processedStep.messageList && processedStep.messageList.length > 0) {
        const textMessage = processedStep.messageList.find(msg => msg.type === 'TEXT');
        if (textMessage && textMessage.params && textMessage.params.content) {
          initialValues.actionParams.content = textMessage.params.content;
        } else if (actionParams.text) {
          // 回退到actionParams.text
          initialValues.actionParams.content = actionParams.text;
        }
      } else if (actionParams.text) {
        // 如果没有messageList但有actionParams.text
        initialValues.actionParams.content = actionParams.text;
      }
      
      // 设置消息列表，如果解析出了messageList，就使用它
      if (processedStep.messageList && processedStep.messageList.length > 0) {
        setMessageList([...processedStep.messageList]);
      }
    } 
    // 为TAG类型特别处理
    else if (processedStep.type === 'TAG' && actionParams.tagId) {
      initialValues.actionParams.tagId = actionParams.tagId;
    }
    // 为MOMENTS类型特别处理
    else if (processedStep.type === 'MOMENTS') {
      if (processedStep.momentsList && processedStep.momentsList.length > 0) {
        // 查找文本类型的朋友圈内容
        const textItem = processedStep.momentsList.find(item => item.type === 'MOMENT_TEXT');
        if (textItem && textItem.params && textItem.params.content) {
          initialValues.actionParams.content = textItem.params.content;
        } else if (actionParams.text && actionParams.text.content) {
          // 回退到actionParams.text.content
          initialValues.actionParams.content = actionParams.text.content;
        }
      } else if (actionParams.text && actionParams.text.content) {
        // 如果没有momentsList但有actionParams.text.content
        initialValues.actionParams.content = actionParams.text.content;
      }
    }
    
    console.log('编辑步骤，设置表单初始值:', initialValues);
    stepForm.setFieldsValue(initialValues);
    
    setIsStepModalVisible(true);
  };

  // 复制步骤
  const handleCopyStep = (step) => {
    const newStep = {
      ...step,
      id: undefined,
      stepCode: generateUniqueStepCode(),
      name: `${step.name} 副本`,
      scheduledDate: step.scheduledDate,
      scheduledTime: step.scheduledTime,
      _edited: true,
    };
    
    setSteps([...steps, newStep]);
    message.success('复制成功');
  };

  // 删除步骤
  const handleDeleteStep = (stepIndex) => {
    const newSteps = [...steps];
    newSteps.splice(stepIndex, 1);
    setSteps(newSteps);
    message.success('删除成功');
  };

  // 保存步骤
  const handleSaveStep = (stepData) => {
    stepForm.validateFields().then(values => {
      // 从 stepForm 的 values 中获取基础字段，但 type 和 actionType 将主要依赖 stepData
      const { name, description } = values;
      const { scheduledDate, scheduledTime } = stepData;
      console.log('保存步骤，表单值 (values):', values);
      console.log('保存步骤，传入的 stepData:', stepData);
      
      // 使用 stepData 中的 type 和 actionType 作为主要来源
      // stepData 由 StepModal 精心构建，已包含正确的类型信息
      let stepType = stepData.type;
      let stepActionType = stepData.actionType;

      // 对于 TAG 类型，如果 actionType 仍然需要从表单中获取（例如 ADD_TAG/REMOVE_TAG）
      // 且 stepData 中没有提供，可以保留这部分逻辑
      if (stepType === 'TAG' && !stepActionType) {
          stepActionType = values.actionType || 'UPDATE_TAG';
      }

      // 构建步骤对象（确保结构一致性）
      let newStep = {
        // 如果是编辑模式，保留ID
        ...(currentStep && currentStep.id ? { id: currentStep.id } : {}),
        
        // 基本字段信息
        name, // 来自表单 values
        type: stepType, // 来自 stepData
        actionType: stepActionType, // 来自 stepData (或 TAG 的特殊处理)
        description: description || '', // 来自表单 values
        // 使用标准化函数处理日期和时间
        scheduledDate: normalizeDateToDayjs(scheduledDate), // 来自表单 values
        scheduledTime: normalizeTimeToDayjs(scheduledTime), // 来自表单 values
        
        // 无论是新建还是编辑，都生成新的stepCode
        stepCode: generateUniqueStepCode(),
        
        // 总是将步骤标记为已编辑
        _edited: true,
        
        // 直接使用从StepModal返回的数据
        actionParams: stepData.actionParams || {},
        
        // 记录类型变更历史，如果有原始步骤
        ...(currentStep && currentStep.type !== stepType ? { _previousType: currentStep.type } : {})
      };
      
      // 保留原始的actionParams（如果有）
      if (currentStep && currentStep.originalActionParams) {
        newStep.originalActionParams = currentStep.originalActionParams;
      }

      // 保留stepData中的目标规则和执行人信息
      if (stepData.targetRules && stepData.targetRules.length > 0) {
        newStep.targetRules = [...stepData.targetRules];
        console.log('保留步骤级别目标规则:', newStep.targetRules);
      }
      
      if (stepData.employeeIds && stepData.employeeIds.length > 0) {
        newStep.employeeIds = [...stepData.employeeIds];
        console.log('保留步骤级别执行人:', newStep.employeeIds);
      }
      
      console.log('初始化newStep对象:', {
        type: newStep.type,
        actionType: newStep.actionType,
        targetRules: newStep.targetRules,
        employeeIds: newStep.employeeIds
      });
      
      // 处理MESSAGE类型的特殊情况 - 主要依赖stepData.type
      if (stepType === 'MESSAGE') {
        // 确保设置正确的actionType
        newStep.actionType = 'SEND_MESSAGE'; // 再次确认
        
        // 设置消息列表
        if (stepData.messageList && stepData.messageList.length > 0) {
          newStep.messageList = stepData.messageList;
          console.log('使用stepData中的messageList:', newStep.messageList.length);
        } else if (newStep.actionParams && newStep.actionParams.content) {
          // 如果有content但没有消息列表，创建一个TEXT类型的消息
          newStep.messageList = [{
            id: 'text-' + Date.now(),
            type: 'TEXT',
            params: {
              content: newStep.actionParams.content
            }
          }];
          console.log('从content创建messageList');
        } else {
          newStep.messageList = []; // 确保为空数组
          console.log('初始化空messageList');
        }
        
        // 清除momentsList(如果有)，避免数据混淆
        if (newStep.momentsList) {
          console.log('MESSAGE类型：删除momentsList');
          newStep.momentsList = undefined;
        }
      }
      
      // 处理MOMENTS类型的特殊情况 - 主要依赖stepData.type
      if (stepType === 'MOMENTS') {
        // 确保设置正确的actionType
        newStep.actionType = 'SEND_MOMENT'; // 再次确认
        
        // 设置朋友圈列表
        if (stepData.momentsList && stepData.momentsList.length > 0) {
          newStep.momentsList = stepData.momentsList;
          console.log('使用stepData中的momentsList:', newStep.momentsList.length);
        } else if (newStep.actionParams && newStep.actionParams.text && newStep.actionParams.text.content) {
          // 如果有content但没有朋友圈列表，创建一个MOMENT_TEXT类型的项目
          newStep.momentsList = [{
            id: 'moment-text-' + Date.now(),
            type: 'MOMENT_TEXT',
            params: {
              content: newStep.actionParams.text.content
            }
          }];
          console.log('从text.content创建momentsList');
        } else if (newStep.actionParams && newStep.actionParams.content) {
          // 备用：直接使用content字段(如果存在)
          newStep.momentsList = [{
            id: 'moment-text-' + Date.now(),
            type: 'MOMENT_TEXT',
            params: {
              content: newStep.actionParams.content
            }
          }];
          console.log('从content创建momentsList');
        } else {
          newStep.momentsList = []; // 确保为空数组
          console.log('初始化空momentsList');
        }
        
        // 清除messageList(如果有)，避免数据混淆
        if (newStep.messageList) {
          console.log('MOMENTS类型：删除messageList');
          newStep.messageList = undefined;
        }
      }
      
      // 处理TAG类型的特殊情况 (actionType 已在前面通过 stepData 或 values.actionType 设置)
      if (stepType === 'TAG') {
        // 确保设置正确的actionType
        if (!newStep.actionType) {
            newStep.actionType = values.actionType || 'UPDATE_TAG';
            console.log('TAG类型：设置默认actionType为', newStep.actionType);
        }
      }
      
      // 记录转换后的结果，以便调试
      console.log('保存步骤，最终步骤类型信息:', {
        initialType: stepData.type,
        finalType: newStep.type,
        initialActionType: stepData.actionType,
        finalActionType: newStep.actionType
      });
      
      // 最终检查确保类型一致性 - 这是一个保护机制
      if (newStep.type !== stepData.type) {
        console.warn(`类型不一致警告! stepData.type(${stepData.type}) 与 newStep.type(${newStep.type}) 不同，强制使用 stepData.type`);
        newStep.type = stepData.type;
        
        // 根据强制设置的类型，确保actionType也一致
        if (newStep.type === 'MESSAGE') {
          newStep.actionType = 'SEND_MESSAGE';
        } else if (newStep.type === 'MOMENTS') {
          newStep.actionType = 'SEND_MOMENT';
        } else if (newStep.type === 'TAG') {
          // TAG类型保持原样，因为具体操作可能是ADD_TAG或REMOVE_TAG
        }
      }
      
      // 更新步骤列表
      if (currentStep) {
        // 编辑模式：更新现有步骤
        const index = steps.findIndex(s => s.stepCode === currentStep.stepCode);
        if (index !== -1) {
          const newSteps = [...steps];
          newSteps[index] = newStep;
          setSteps(newSteps);
        } else {
          // 如果找不到对应的stepCode，就添加为新步骤
          setSteps([...steps, newStep]);
        }
      } else {
        // 添加模式：插入新步骤
        setSteps([...steps, newStep]);
      }
      
      // 关闭模态框并重置
      setIsStepModalVisible(false);
      stepForm.resetFields();
      setCurrentStep(null);
      setMessageList([]);
      setPreviewUrlsState([]);
    }).catch(error => {
      console.error('表单验证失败:', error);
      message.error('表单验证失败，请检查输入');
    });
  };

  const handleStepModalCancel = () => {
    setIsStepModalVisible(false);
    stepForm.resetFields();
    setCurrentStep(null);
    setMessageList([]);
    setPreviewUrlsState([]);
  };

  return {
    currentStep,
    setCurrentStep,
    isStepModalVisible,
    setIsStepModalVisible,
    stepForm,
    handleAddStep,
    handleEditStep,
    handleCopyStep,
    handleDeleteStep,
    handleSaveStep,
    handleStepModalCancel,
  };
}; 